import React from "react";
import { Box, Stack, Typography, IconButton, Tooltip, Switch } from "@mui/material";
import {
  MdTwoWheeler,
  MdPhone,
  MdBadge,
  MdOpenInNew,
  MdEdit,
  MdDelete,
  MdPowerSettingsNew,
} from "react-icons/md";

import { StatusChip } from "components/ui";
import { T, tint, inr } from "theme/tokens";

/**
 * The directory's columns.
 *
 * Related fields are grouped into one two-line cell rather than given a column
 * each — the two screens this merges had fourteen single-value columns between
 * them, which is a horizontal scroll nobody reads.
 */

const mono = { fontFamily: "monospace", fontSize: "0.72rem", color: T.subtle };

/** How a partner's duty state reads in the table. Must match the map legend. */
export const DUTY = {
  onDuty: { label: "On duty", tone: T.success },
  offDuty: { label: "Off duty", tone: T.secondary },
  unverified: { label: "Unverified", tone: T.warning },
};

const day = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

export default function buildColumns({
  onOpenConsole,
  onEdit,
  onDelete,
  onForceOffline,
  onToggleVerified,
  busyId,
}) {
  return [
    {
      field: "fullname",
      headerName: "Partner",
      flex: 1.4,
      minWidth: 220,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0, py: 0.7 }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.5,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: tint(DUTY[row.dutyState]?.tone || T.muted, 0.12),
              color: DUTY[row.dutyState]?.tone || T.muted,
            }}
          >
            <MdTwoWheeler size={17} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.83rem", fontWeight: 600, color: T.ink }}>
              {row.fullname || "Unnamed partner"}
            </Typography>
            <Typography sx={{ ...mono, fontSize: "0.66rem", wordBreak: "break-all" }}>
              {row.partnerId || "—"}
            </Typography>
          </Box>
        </Stack>
      ),
    },
    {
      field: "phone",
      headerName: "Contact",
      width: 150,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.7} alignItems="center">
          <MdPhone size={13} color={T.subtle} />
          <Typography sx={{ fontFamily: "monospace", fontSize: "0.78rem", color: T.ink }}>
            {row.phone || "—"}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "vehicleNumber",
      headerName: "Vehicle & licence",
      width: 175,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack spacing={0.3} sx={{ py: 0.7 }}>
          <Stack direction="row" spacing={0.7} alignItems="center">
            <MdTwoWheeler size={13} color={row.vehicleNumber ? T.secondary : T.subtle} />
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: "0.76rem",
                color: row.vehicleNumber ? T.ink : T.subtle,
              }}
            >
              {row.vehicleNumber || "No vehicle"}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.7} alignItems="center">
            <MdBadge size={13} color={row.drivingLicense ? T.secondary : T.subtle} />
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: "0.76rem",
                color: row.drivingLicense ? T.ink : T.subtle,
              }}
            >
              {row.drivingLicense || "No licence"}
            </Typography>
          </Stack>
        </Stack>
      ),
    },
    {
      // Approving a partner is the action this screen exists for, so the
      // switch stays in the row rather than moving into the console.
      field: "isVerified",
      headerName: "Approved",
      width: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack
          alignItems="center"
          spacing={0.2}
          sx={{ py: 0.7 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Switch
            size="small"
            checked={Boolean(row.isVerified)}
            disabled={busyId === row.partnerId}
            onChange={(e) => onToggleVerified(row, e.target.checked)}
          />
          <Typography
            sx={{
              fontSize: "0.65rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: row.isVerified ? T.success : T.warning,
            }}
          >
            {row.isVerified ? "Verified" : "Pending"}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "dutyState",
      headerName: "Duty",
      width: 165,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack spacing={0.4} sx={{ py: 0.7 }} alignItems="flex-start">
          <StatusChip
            tone={DUTY[row.dutyState]?.tone}
            label={DUTY[row.dutyState]?.label || row.dutyState}
          />
          {/* Online and accepting are separate switches and a partner can be
              one without the other, so both are shown rather than collapsed. */}
          <Stack direction="row" spacing={0.5}>
            <StatusChip
              tone={row.loginStatus ? T.success : T.subtle}
              label={row.loginStatus ? "Online" : "Offline"}
            />
            <StatusChip
              tone={row.isTakingOrders ? T.info : T.subtle}
              label={row.isTakingOrders ? "Accepting" : "Paused"}
            />
          </Stack>
        </Stack>
      ),
    },
    {
      field: "totalOrders",
      headerName: "Orders",
      width: 95,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography
          sx={{
            fontSize: "0.85rem",
            fontWeight: 600,
            color: row.totalOrders ? T.ink : T.subtle,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {row.totalOrders ?? 0}
        </Typography>
      ),
    },
    {
      field: "totalBalance",
      headerName: "Owed",
      width: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography
          sx={{
            fontSize: "0.85rem",
            fontWeight: 700,
            fontVariantNumeric: "tabular-nums",
            color: row.totalBalance > 0 ? T.warning : T.subtle,
          }}
        >
          {inr(row.totalBalance)}
        </Typography>
      ),
    },
    {
      field: "hasDocuments",
      headerName: "KYC & bank",
      width: 155,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack spacing={0.4} sx={{ py: 0.7 }} alignItems="flex-start">
          <StatusChip
            tone={row.hasDocuments ? T.success : T.error}
            label={row.hasDocuments ? "Docs on file" : "No docs"}
          />
          <StatusChip
            tone={row.hasBankDetails ? T.success : T.subtle}
            label={row.hasBankDetails ? row.bankName || "Bank on file" : "No bank"}
          />
        </Stack>
      ),
    },
    {
      field: "joinedAt",
      headerName: "Joined",
      width: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography sx={{ fontSize: "0.76rem", color: T.muted }}>
          {day(row.joinedAt) || "—"}
        </Typography>
      ),
    },
    {
      field: "location",
      headerName: "Location",
      width: 110,
      sortable: false,
      renderCell: ({ row }) =>
        row.coordinates ? (
          <Typography sx={{ fontSize: "0.7rem", color: T.success, fontWeight: 600 }}>
            Last seen
            <Box component="span" sx={{ display: "block", ...mono, fontSize: "0.62rem" }}>
              {row.coordinates.lat.toFixed(3)}, {row.coordinates.lng.toFixed(3)}
            </Box>
          </Typography>
        ) : (
          <Typography sx={{ fontSize: "0.7rem", color: T.subtle, fontWeight: 600 }}>
            Never seen
          </Typography>
        ),
    },
    {
      field: "actions",
      headerName: "",
      width: 165,
      sortable: false,
      filterable: false,
      // Clicking a row opens the console, so nothing in this cell may bubble.
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.3} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Open partner console">
            <IconButton size="small" onClick={() => onOpenConsole(row)} sx={{ color: T.primary }}>
              <MdOpenInNew size={16} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Edit details">
            <IconButton size="small" onClick={() => onEdit(row)} sx={{ color: T.muted }}>
              <MdEdit size={16} />
            </IconButton>
          </Tooltip>

          {/* An app that crashed leaves someone showing as available who is
              not, so ops needs to be able to correct it — but only ever
              downwards. Nobody marks another person "on shift" from here. */}
          <Tooltip
            title={
              row.loginStatus ? "Force offline" : "Already offline — only the app signs in"
            }
          >
            <span>
              <IconButton
                size="small"
                disabled={!row.loginStatus}
                onClick={() => onForceOffline(row)}
                sx={{ color: T.warning }}
              >
                <MdPowerSettingsNew size={16} />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Delete partner">
            <IconButton size="small" onClick={() => onDelete(row)} sx={{ color: T.error }}>
              <MdDelete size={16} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];
}
